import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { runningBundle as running, BUNDLE_RE as MAIN_RE, report, flush } from '../utils/telemetry';

/* A phone keeps the dashboard open for days: the browser (or the home-screen app)
   wakes the tab from memory without reloading, so after a deploy it goes on running
   the old build. index.html is served no-cache, but nothing asks for it until a real
   reload, and an old page chunk that is already cached never 404s, so lazyPage()'s
   reload never fires either. The farm manager saw new Manual Entry options missing
   until he refreshed (5 Nov 2026).

   So: on waking and every few minutes, fetch index.html and compare the main bundle
   it names with the one running. On a new build, reload at the next page change
   (nothing on screen to lose), right away if the tab sat in the background for
   AWAY_RELOAD_MS (a draft that old is stale anyway), else offer a bar to tap. */

const CHECK_EVERY_MS = 5 * 60 * 1000;
const AWAY_RELOAD_MS = 30 * 60 * 1000;

async function newBuildOut() {
  try {
    const res = await fetch('/index.html', { cache: 'no-store', credentials: 'same-origin' });
    if (!res.ok) return false;
    const m = (await res.text()).match(MAIN_RE);
    return Boolean(m && m[0] !== running);
  } catch {
    return false;                   // offline / server restarting: try again later
  }
}

const Bar = styled.button`
  position: fixed;
  left: 50%;
  bottom: ${p => p.theme.spacing.lg};
  transform: translateX(-50%);
  z-index: 1003;
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.lg};
  border: none;
  border-radius: ${p => p.theme.borderRadius.full};
  background: ${p => p.theme.colors.primary};
  color: ${p => p.theme.colors.textInverse};
  font-family: ${p => p.theme.fonts.primary};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.medium};
  box-shadow: ${p => p.theme.shadows.lg};
  cursor: pointer;
  white-space: nowrap;
`;

export default function BuildUpdate() {
  const [ready, setReady] = useState(false);
  const { pathname } = useLocation();
  const firstPath = useRef(pathname);
  const hiddenAt = useRef(null);

  useEffect(() => {
    if (!running) return undefined;
    let stopped = false;
    let reported = false;
    const check = async (cameBackAfter = 0) => {
      if (stopped || !(await newBuildOut()) || stopped) return;
      // How long this tab ran an old build is what tells us whether the check is enough.
      const reload = cameBackAfter >= AWAY_RELOAD_MS;
      if (!reported || reload) {
        report('stale_build', { ms: performance.now(), details: { away_ms: cameBackAfter, action: reload ? 'reload' : 'bar' } });
        reported = true;
      }
      if (reload) { flush(); window.location.reload(); } else setReady(true);
    };
    const onVisibility = () => {
      if (document.hidden) { hiddenAt.current = Date.now(); return; }
      const away = hiddenAt.current ? Date.now() - hiddenAt.current : 0;
      hiddenAt.current = null;
      check(away);
    };
    const onFocus = () => check();
    const timer = setInterval(() => { if (!document.hidden) check(); }, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onFocus);   // back-forward cache restores
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onFocus);
    };
  }, []);

  // Leaving a page throws its state away anyway: load the new build there.
  useEffect(() => {
    if (ready && pathname !== firstPath.current) { flush(); window.location.reload(); }
    firstPath.current = pathname;
  }, [ready, pathname]);

  if (!ready) return null;
  return (
    <Bar type="button" onClick={() => window.location.reload()}>
      Update ready · tap to load
    </Bar>
  );
}
